import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  X, 
  AlertCircle,
  RefreshCw,
  Calendar,
  Plus,
  Trash2,
  Receipt,
  Calculator,
  Settings2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CurrencyInput from "../components/CurrencyInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaxesPage = () => {
  const { yearParam } = useParams();
  const navigate = useNavigate();
  
  const currentYear = new Date().getFullYear();
  
  const [year, setYear] = useState(() => {
    if (yearParam) {
      const parsed = parseInt(yearParam);
      if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
        return parsed;
      }
    }
    return currentYear;
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [originalCards, setOriginalCards] = useState([]);
  const [cards, setCards] = useState([]);
  
  // Dialogs
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCardType, setNewCardType] = useState("");
  const [selectedPresetType, setSelectedPresetType] = useState("custom");
  const [showScheduler, setShowScheduler] = useState(false);
  const [schedulerCardId, setSchedulerCardId] = useState(null);
  const [deleteCardConfirm, setDeleteCardConfirm] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState({
    total_amount: 0,
    terms: 12,
    frequency: "monthly",
    start_date: `${currentYear}-01-01`
  });

  // Check which preset types are already used
  const usedTaxTypes = cards.map(c => c.tax_type);
  const availablePresets = [
    { value: "BTW", label: "BTW (VAT)", disabled: usedTaxTypes.includes("BTW") },
    { value: "VPB", label: "VPB (Corporate Tax)", disabled: usedTaxTypes.includes("VPB") },
    { value: "custom", label: "Custom type...", disabled: false }
  ];

  useEffect(() => { document.title = "Taxes - Floatly"; }, []);

  // Update URL when year changes
  useEffect(() => {
    const newPath = `/taxes/${year}`;
    if (window.location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [year, navigate]);

  // Fetch data for year
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/taxes/${year}`);
      const fetchedCards = response.data.cards;
      
      setOriginalCards(JSON.parse(JSON.stringify(fetchedCards)));
      setCards(fetchedCards);
    } catch (err) {
      console.error("Error fetching tax data:", err);
      setError("Failed to load tax data");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if there are changes
  const hasChanges = JSON.stringify(cards) !== JSON.stringify(originalCards);

  // Navigation
  const goToPrevYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y - 1);
  };

  const goToNextYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y + 1);
  };

  const goToCurrentYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(currentYear);
  };

  // Handle entry changes
  const handleEntryChange = (cardIndex, entryIndex, field, value) => {
    setCards(prev => {
      const newCards = [...prev];
      const newEntries = [...newCards[cardIndex].entries];
      newEntries[entryIndex] = { ...newEntries[entryIndex], [field]: value };
      newCards[cardIndex] = { ...newCards[cardIndex], entries: newEntries };
      return newCards;
    });
  };

  // Add new entry to a card
  const handleAddEntry = (cardIndex) => {
    setCards(prev => {
      const newCards = [...prev];
      const card = newCards[cardIndex];
      const newEntry = {
        id: crypto.randomUUID(),
        subtype: card.tax_type === "BTW" ? `Q${card.entries.length + 1}` : `${card.tax_type} ${card.entries.length + 1}`,
        payment_type: "Te betalen",
        amount: 0,
        payment_date: `${year}-12-31`
      };
      newCards[cardIndex] = { ...card, entries: [...card.entries, newEntry] };
      return newCards;
    });
  };

  // Delete entry from a card
  const handleDeleteEntry = (cardIndex, entryIndex) => {
    const card = cards[cardIndex];
    if (card.entries.length <= 1) {
      toast.error("Cannot delete the last entry");
      return;
    }
    
    setCards(prev => {
      const newCards = [...prev];
      const newEntries = newCards[cardIndex].entries.filter((_, i) => i !== entryIndex);
      newCards[cardIndex] = { ...newCards[cardIndex], entries: newEntries };
      return newCards;
    });
  };

  // Add new tax card
  const handleAddCard = async () => {
    // Determine the actual tax type to use
    const taxType = selectedPresetType === "custom" ? newCardType.trim() : selectedPresetType;
    
    if (!taxType) {
      toast.error("Please enter a tax type name");
      return;
    }
    
    // Check if already exists
    if (cards.some(c => c.tax_type.toLowerCase() === taxType.toLowerCase())) {
      toast.error("This tax type already exists");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/taxes/${year}/cards`, {
        tax_type: taxType,
        preset_type: selectedPresetType !== "custom" ? selectedPresetType : null
      });
      
      setCards(prev => [...prev, response.data]);
      setOriginalCards(prev => [...prev, response.data]);
      setNewCardType("");
      setSelectedPresetType("custom");
      setShowAddCard(false);
      toast.success(`Tax card "${taxType}" created`);
    } catch (err) {
      console.error("Error creating tax card:", err);
      toast.error(err.response?.data?.detail || "Failed to create tax card");
    }
  };

  // Delete tax card
  const handleDeleteCard = async (cardIndex) => {
    const card = cards[cardIndex];
    setDeleteCardConfirm({ card, cardIndex });
  };

  // Confirm delete
  const confirmDeleteCard = async () => {
    if (!deleteCardConfirm) return;
    
    const { card, cardIndex } = deleteCardConfirm;
    
    try {
      await axios.delete(`${API}/taxes/${year}/cards/${card.id}`);
      
      setCards(prev => prev.filter((_, i) => i !== cardIndex));
      setOriginalCards(prev => prev.filter(c => c.id !== card.id));
      toast.success(`Tax card "${card.tax_type}" deleted`);
    } catch (err) {
      console.error("Error deleting tax card:", err);
      toast.error(err.response?.data?.detail || "Failed to delete tax card");
    } finally {
      setDeleteCardConfirm(null);
    }
  };

  // Generate payment schedule for any tax type
  const handleGenerateSchedule = async () => {
    if (!schedulerCardId) return;
    
    try {
      const response = await axios.post(`${API}/taxes/${year}/schedule`, {
        card_id: schedulerCardId,
        total_amount: parseFloat(scheduleConfig.total_amount) || 0,
        terms: parseInt(scheduleConfig.terms) || 12,
        frequency: scheduleConfig.frequency,
        start_date: scheduleConfig.start_date
      });
      
      // Find the card and update its entries
      setCards(prev => {
        const newCards = [...prev];
        const cardIndex = newCards.findIndex(c => c.id === schedulerCardId);
        if (cardIndex !== -1) {
          newCards[cardIndex] = { 
            ...newCards[cardIndex], 
            entries: response.data.entries 
          };
        }
        return newCards;
      });
      
      setShowScheduler(false);
      setSchedulerCardId(null);
      toast.success(`Generated ${response.data.entries.length} payment terms at €${response.data.amount_per_term.toFixed(2)} each`);
    } catch (err) {
      console.error("Error generating schedule:", err);
      toast.error(err.response?.data?.detail || "Failed to generate schedule");
    }
  };

  // Open scheduler for a specific card
  const openScheduler = (card) => {
    setSchedulerCardId(card.id);
    setScheduleConfig({
      total_amount: 0,
      terms: 12,
      frequency: "monthly",
      start_date: `${year}-01-01`
    });
    setShowScheduler(true);
  };

  // Get the card being scheduled
  const getSchedulerCard = () => {
    return cards.find(c => c.id === schedulerCardId);
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      await axios.patch(`${API}/taxes/${year}/bulk`, {
        cards: cards.map(card => ({
          id: card.id,
          tax_type: card.tax_type,
          entries: card.entries.map(e => ({
            id: e.id,
            subtype: e.subtype,
            payment_type: e.payment_type,
            amount: parseFloat(e.amount) || 0,
            payment_date: e.payment_date
          }))
        }))
      });
      
      setOriginalCards(JSON.parse(JSON.stringify(cards)));
      toast.success("Tax data saved successfully");
    } catch (err) {
      console.error("Error saving tax data:", err);
      toast.error("Failed to save tax data");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setCards(JSON.parse(JSON.stringify(originalCards)));
    toast.info("Changes discarded");
  };

  // Format currency for display
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Calculate totals
  const calculateCardTotal = (card) => {
    return card.entries.reduce((sum, entry) => {
      const amt = parseFloat(entry.amount) || 0;
      return entry.payment_type === "Te ontvangen" ? sum - amt : sum + amt;
    }, 0);
  };

  const totalTaxes = cards.reduce((sum, card) => sum + calculateCardTotal(card), 0);

  // Get icon for tax type
  const getCardIcon = (taxType) => {
    switch (taxType) {
      case "BTW": return <Receipt className="w-5 h-5" />;
      case "VPB": return <Calculator className="w-5 h-5" />;
      default: return <Settings2 className="w-5 h-5" />;
    }
  };

  // Get card color
  const getCardColor = (taxType) => {
    switch (taxType) {
      case "BTW": return "border-l-blue-500";
      case "VPB": return "border-l-amber-500";
      default: return "border-l-slate-500";
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchData} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="taxes-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Taxes
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                VAT, corporate tax, and other tax obligations
              </p>
              <LastUpdated page="taxes" />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Year Total</p>
              <p className={`text-2xl font-semibold ${totalTaxes < 0 ? 'text-green-600' : ''}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatCurrency(totalTaxes)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Year Navigator */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30 shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevYear}
                className="rounded-full"
                data-testid="prev-year-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <h2 className="text-xl font-semibold min-w-[100px] text-center" data-testid="current-year">
                {year}
              </h2>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextYear}
                className="rounded-full"
                data-testid="next-year-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCard(true)}
                className="rounded-full"
                data-testid="add-card-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Tax Type
              </Button>
              {year !== currentYear && (
                <Button
                  variant="outline"
                  onClick={goToCurrentYear}
                  className="rounded-full"
                  data-testid="current-year-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {currentYear}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Tax Cards */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4 sm:gap-6" data-testid="tax-cards-grid">
            {cards.map((card, cardIndex) => (
              <div
                key={card.id}
                className={`bg-white border rounded-xl shadow-sm overflow-hidden border-l-4 flex-1 basis-[420px] max-w-full ${getCardColor(card.tax_type)}`}
                data-testid={`tax-card-${card.tax_type}`}
              >
                {/* Card Header */}
                <div className="px-3 sm:px-4 py-3 border-b bg-muted/20 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="text-muted-foreground shrink-0">
                      {getCardIcon(card.tax_type)}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate">{card.tax_type}</h3>
                      <p className="text-xs text-muted-foreground">
                        {card.entries.length} {card.entries.length === 1 ? 'entry' : 'entries'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openScheduler(card)}
                      className="text-xs px-2 sm:px-3"
                      data-testid={`schedule-button-${card.tax_type}`}
                    >
                      <Calculator className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">Schedule</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCard(cardIndex)}
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-card-${card.tax_type}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Entries Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-0">
                    <thead className="bg-muted/10 text-xs text-muted-foreground uppercase">
                      <tr>
                        <th className="text-left px-2 sm:px-3 py-2 min-w-[60px]">Period</th>
                        <th className="text-left px-2 sm:px-3 py-2 min-w-[80px]">Type</th>
                        <th className="text-right px-2 sm:px-3 py-2 min-w-[70px]">Amount</th>
                        <th className="text-center px-2 sm:px-3 py-2 min-w-[100px]">Date</th>
                        <th className="w-[32px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.entries.map((entry, entryIndex) => (
                        <tr 
                          key={entry.id} 
                          className="border-t hover:bg-muted/30 transition-colors"
                          data-testid={`entry-${card.tax_type}-${entryIndex}`}
                        >
                          <td className="px-2 sm:px-3 py-2">
                            <Input
                              type="text"
                              value={entry.subtype || ""}
                              onChange={(e) => handleEntryChange(cardIndex, entryIndex, "subtype", e.target.value)}
                              className="h-8 text-xs font-medium min-w-0"
                              placeholder="Q1"
                              data-testid={`entry-subtype-${card.tax_type}-${entryIndex}`}
                            />
                          </td>
                          <td className="px-2 sm:px-3 py-2">
                            <Select
                              value={entry.payment_type}
                              onValueChange={(val) => handleEntryChange(cardIndex, entryIndex, "payment_type", val)}
                            >
                              <SelectTrigger className="h-8 text-xs min-w-0" data-testid={`entry-type-${card.tax_type}-${entryIndex}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Te betalen">Te betalen</SelectItem>
                                <SelectItem value="Te ontvangen">Te ontvangen</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 sm:px-3 py-2">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
                              <CurrencyInput
                                value={entry.amount || 0}
                                onChange={(val) => handleEntryChange(cardIndex, entryIndex, "amount", val)}
                                className="pl-5 h-8 text-xs min-w-0"
                                placeholder="0"
                                data-testid={`entry-amount-${card.tax_type}-${entryIndex}`}
                              />
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-2">
                            <Input
                              type="date"
                              value={entry.payment_date || ""}
                              onChange={(e) => handleEntryChange(cardIndex, entryIndex, "payment_date", e.target.value)}
                              className="font-mono h-8 text-xs min-w-0"
                              data-testid={`entry-date-${card.tax_type}-${entryIndex}`}
                            />
                          </td>
                          <td className="px-1 py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteEntry(cardIndex, entryIndex)}
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              disabled={card.entries.length <= 1}
                              data-testid={`delete-entry-${card.tax_type}-${entryIndex}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card Footer */}
                <div className="px-3 sm:px-4 py-3 border-t bg-muted/30 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddEntry(cardIndex)}
                    className="text-xs"
                    data-testid={`add-entry-${card.tax_type}`}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Entry
                  </Button>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">Total: </span>
                    <span className={`font-mono font-semibold ${calculateCardTotal(card) < 0 ? 'text-green-600' : ''}`}>
                      {formatCurrency(calculateCardTotal(card))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            Unsaved changes
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save All"}
          </Button>
        </div>
      )}

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={(open) => { 
        setShowAddCard(open); 
        if (!open) { 
          setNewCardType(""); 
          setSelectedPresetType("custom"); 
        } 
      }}>
        <DialogContent className="sm:max-w-md" data-testid="add-card-dialog">
          <DialogHeader>
            <DialogTitle>Add Tax Type</DialogTitle>
            <DialogDescription>
              Select a predefined tax type or create a custom one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-type">Tax Type</Label>
              <Select
                value={selectedPresetType}
                onValueChange={(val) => {
                  setSelectedPresetType(val);
                  if (val !== "custom") {
                    setNewCardType("");
                  }
                }}
              >
                <SelectTrigger id="preset-type" data-testid="preset-type-select">
                  <SelectValue placeholder="Select tax type..." />
                </SelectTrigger>
                <SelectContent>
                  {availablePresets.map(preset => (
                    <SelectItem 
                      key={preset.value} 
                      value={preset.value}
                      disabled={preset.disabled}
                    >
                      {preset.label}
                      {preset.disabled && " (already exists)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedPresetType === "custom" && (
              <div className="space-y-2">
                <Label htmlFor="tax-type">Custom Tax Type Name</Label>
                <Input
                  id="tax-type"
                  value={newCardType}
                  onChange={(e) => setNewCardType(e.target.value)}
                  placeholder="e.g., Dividend Tax, Property Tax"
                  data-testid="new-card-type-input"
                />
              </div>
            )}
            
            {selectedPresetType !== "custom" && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="text-muted-foreground">
                  {selectedPresetType === "BTW" && (
                    <>This will create <strong>4 quarterly entries</strong> (Q1-Q4) with default payment dates.</>
                  )}
                  {selectedPresetType === "VPB" && (
                    <>This will create <strong>10 monthly payment terms</strong> for corporate tax.</>
                  )}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCard(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCard} 
              disabled={selectedPresetType === "custom" && !newCardType.trim()}
              data-testid="confirm-add-card"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog (for any tax type) */}
      <Dialog open={showScheduler} onOpenChange={(open) => { setShowScheduler(open); if (!open) setSchedulerCardId(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Generate {getSchedulerCard()?.tax_type} Payment Schedule</DialogTitle>
            <DialogDescription>
              Automatically generate installment payments for {getSchedulerCard()?.tax_type}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-amount">Total Amount (€)</Label>
              <Input
                id="schedule-amount"
                type="number"
                step="0.01"
                value={scheduleConfig.total_amount}
                onChange={(e) => setScheduleConfig(prev => ({ ...prev, total_amount: e.target.value }))}
                placeholder="Enter total amount"
                data-testid="schedule-amount-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-terms">Number of Terms</Label>
                <Input
                  id="schedule-terms"
                  type="number"
                  min="1"
                  max="12"
                  value={scheduleConfig.terms}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, terms: parseInt(e.target.value) || 12 }))}
                  data-testid="schedule-terms-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="schedule-frequency">Frequency</Label>
                <Select
                  value={scheduleConfig.frequency}
                  onValueChange={(val) => setScheduleConfig(prev => ({ ...prev, frequency: val }))}
                >
                  <SelectTrigger id="schedule-frequency" data-testid="schedule-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-start">Start Date</Label>
              <Input
                id="schedule-start"
                type="date"
                value={scheduleConfig.start_date}
                onChange={(e) => setScheduleConfig(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="schedule-start-date-input"
              />
            </div>
            {scheduleConfig.total_amount > 0 && scheduleConfig.terms > 0 && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="text-muted-foreground">
                  This will create <strong>{scheduleConfig.terms}</strong> payment{scheduleConfig.terms > 1 ? 's' : ''} of{' '}
                  <strong>{formatCurrency(parseFloat(scheduleConfig.total_amount) / scheduleConfig.terms)}</strong> each.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowScheduler(false); setSchedulerCardId(null); }}>
              Cancel
            </Button>
            <Button onClick={handleGenerateSchedule} data-testid="confirm-schedule">
              <Calculator className="w-4 h-4 mr-2" />
              Generate Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tax Card Confirmation Dialog */}
      <AlertDialog open={!!deleteCardConfirm} onOpenChange={(open) => !open && setDeleteCardConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax card?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the <strong>"{deleteCardConfirm?.card?.tax_type}"</strong> tax card? 
              This will remove all {deleteCardConfirm?.card?.entries?.length || 0} entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteCard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxesPage;
